import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
    Paper,
    Grid,
    Divider,
    IconButton,
    styled,
    Chip,
    Button,
    DialogActions,
    CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { approvalAccept, approvalReject } from "../api/approval";
import { showErrorToast, showSuccessToast } from "../toastUtils";
import { useState } from "react";

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialog-paper": {
        minWidth: "500px",
        maxWidth: "800px",
        width: "fit-content",
        margin: theme.spacing(2),
    },
    "& .MuiDialogContent-root": {
        padding: theme.spacing(3),
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
    },
    "& .MuiDialogTitle-root": {
        padding: theme.spacing(2, 3),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(2, 3),
    }
}));

const ChangeBox = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "#F9FAFB",
    height: "100%",
    fontFamily: "monospace",
    fontSize: 14,
    whiteSpace: "pre-wrap",
    overflow: "auto",
    // maxHeight: "300px"
}));

const ChangeRequestViewModal = ({
    open,
    onClose,
    fetchData,
    title = "Change Request",
    approvalId,
    beforeData,
    afterData,
    requestedBy,
    requestedAt,
    actionType = "Update",
    // onApprove,
    // onReject
}) => {
    const [approveLoading, setApproveLoading] = useState(false)
    const [rejectLoading, setRejectLoading] = useState(false)

    // const formatDate = (dateString) => {
    //     try {
    //         return format(new Date(dateString), "yyyy-MM-dd HH:mm");
    //     } catch (error) {
    //         return dateString;
    //     }
    // };
    function convertUTCToIST(utcDateTimeStr) {
        const utcDate = new Date(utcDateTimeStr + 'Z'); // Ensure it's treated as UTC
        // const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
        // const istDate = new Date(utcDate.getTime() + istOffset);
        const istDate = new Date(utcDate.getTime());
        return istDate.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
    }

    //   // Example usage:
    //   const utcTime = '2025-04-29T12:43:38';
    //   console.log(convertUTCToIST(utcTime));
    //   // Output: "29/4/2025, 6:13:38 pm" (or similar, based on system locale)


    const handleApprove = async () => {
        setApproveLoading(true)
        try {
            const response = await approvalAccept(approvalId);

            if (response?.success) {
                fetchData()
                onClose();
                showSuccessToast(response?.message || 'Approval successful')
            } else {
                console.error('Failed to approve:', response?.message || 'Unknown error');
                showErrorToast(response?.message || 'Unknown error')
            }
        } catch (error) {
            console.error('Error during approval:', error);
            showErrorToast(error.message || 'Error during approval')
        } finally {
            setApproveLoading(false)
        }
    };

    const handleReject = async () => {
        setRejectLoading(true)
        try {
            const response = await approvalReject(approvalId);

            if (response?.success) {
                fetchData()
                onClose();
                showSuccessToast(response?.message || 'Rejection successful')
            } else {
                console.error('Failed to reject:', response?.message || 'Unknown error');
                showErrorToast(response?.message || 'Unknown error')
            }
        } catch (error) {
            console.error('Error during rejection:', error);
            showErrorToast(error.message || 'Error during rejection')
        } finally {
            setRejectLoading(false)
        }
    };


    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            // maxWidth="md"
            fullWidth
            aria-labelledby="change-request-dialog-title"
            scroll="paper"
        >
            <DialogTitle id="change-request-dialog-title" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>{title}</Typography>
                <IconButton aria-label="close" onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent dividers>
                <Box sx={{ mb: 4 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Requested by:
                            </Typography>
                            <Typography variant="body1">
                                {requestedBy || "-"}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Requested at:
                            </Typography>
                            <Typography variant="body1">
                                {convertUTCToIST(requestedAt) || "-"}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Action type:
                            </Typography>
                            <Chip
                                label={actionType.toUpperCase()}
                                variant="outlined"
                                sx={{
                                    backgroundColor:
                                        actionType === 'update' ? '#FEF3E7' :
                                            actionType === 'insert' ? '#E7F7ED' : // light green for success
                                                actionType === 'delete' ? '#FDECEA' : // light red for error
                                                    '#fff', // default fallback

                                    color:
                                        actionType === 'update' ? '#C76D16' :
                                            actionType === 'insert' ? '#2E7D32' : // dark green
                                                actionType === 'delete' ? '#D32F2F' : // dark red
                                                    '#000', // default fallback

                                    borderColor:
                                        actionType === 'update' ? '#FEF3E7' :
                                            actionType === 'insert' ? '#E7F7ED' :
                                                actionType === 'delete' ? '#FDECEA' :
                                                    '#ccc', // default fallback
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Changes
                    </Typography>
                    <Box display="flex" gap={2}>
                        {/* Before Box */}
                        <Box flex={1}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Before
                            </Typography>
                            <ChangeBox>
                                {beforeData === null || (typeof beforeData === "object" && Object.keys(beforeData).length === 0)
                                    ? 'New Data Inserted'
                                    : typeof beforeData === "object"
                                        ? (
                                            <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                                {JSON.stringify(beforeData, null, 2)}
                                            </pre>
                                        )
                                        : beforeData}
                            </ChangeBox>
                        </Box>

                        {/* After Box */}
                        <Box flex={1}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                After
                            </Typography>
                            <ChangeBox>
                                {afterData === null || (typeof afterData === "object" && Object.keys(afterData).length === 0)
                                    ? 'New Data Inserted'
                                    : typeof afterData === "object"
                                        ? (
                                            <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                                {JSON.stringify(afterData, null, 2)}
                                            </pre>
                                        )
                                        : afterData}
                            </ChangeBox>
                        </Box>
                    </Box>

                </Box>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" color="error"
                    disabled={approveLoading}
                    onClick={handleReject}>
                    {rejectLoading ? <CircularProgress size={20} /> : 'Reject'}
                </Button>
                <Button variant="contained" color="primary"
                    disabled={rejectLoading}
                    onClick={handleApprove}
                    sx={{
                        backgroundColor: "#FF6348",
                        color: "#fff"
                    }}
                >
                    {approveLoading ? <CircularProgress size={20} color="#fff" /> : 'Approve'}
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};

export default ChangeRequestViewModal;




// <Grid container spacing={2}>
// <Grid item xs={6}>
//     <Typography variant="subtitle2" sx={{ mb: 1 }}>
//         Before
//     </Typography>
//     <ChangeBox>
//         {/* {beforeData === null || (typeof beforeData === "object" && Object.keys(beforeData).length === 0)
//             ? 'New Data Inserted'
//             : beforeData} */}
//         {beforeData === null || (typeof beforeData === "object" && Object.keys(beforeData).length === 0)
//             ? 'New Data Inserted'
//             : typeof beforeData === "object"
//                 ? (
//                     <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
//                         {JSON.stringify(beforeData, null, 2)}
//                     </pre>
//                 )
//                 : beforeData}


//     </ChangeBox>
// </Grid>
// <Grid item xs={6}>
//     <Typography variant="subtitle2" sx={{ mb: 1 }}>
//         After
//     </Typography>
//     <ChangeBox>
//         {/* {typeof afterData === "object" ? JSON.stringify(afterData, null, 2) : afterData} */}
//         <div>
//             {afterData === null || (typeof afterData === "object" && Object.keys(afterData).length === 0)
//                 ? 'New Data Inserted'
//                 : typeof afterData === "object"
//                     ? (
//                         <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
//                             {JSON.stringify(afterData, null, 2)}
//                         </pre>
//                     )
//                     : afterData}
//         </div>

//     </ChangeBox>
// </Grid>
// </Grid>

